import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Add } from '@mui/icons-material';
import { Box, Menu, MenuItem, CircularProgress } from "@mui/material";
import { useSnackbar } from "../../contexts/CustomSnackbarContext";

import {
  createVariableSet,
  getAllVariableSets,
  deleteVariableSet,
  markVariableSetAsPrimary,
  cloneEnvironmentVariable,
} from '../../redux-store/variableSetReducers/variableSetActions';

import useSearchParams from '../../hooks/useSearchParams';
import MainLayout from '../../layouts/MainLayout';
import VariableSetList from './VariableSetList';
import CustomButton from '../Common/CustomButton';
import { MixpanelService } from '../../utils/mixpanelService';
import { clone } from 'lodash';

const VariableSetListPage = ({ project }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { openSnackbar } = useSnackbar();
  const searchParams = useSearchParams();
  const page = parseInt(searchParams.get('page') || '1');

  const isLoading = useSelector((state) => state.variableSet.isLoading);

  const [pageNumber, setPageNumber] = useState(isNaN(page) ? 1 : page);
  const [variableSetList, setVariableSetList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalVariableSets, setTotalVariableSets] = useState(0);
  const [isLoadingData, setIsLoadingData] = useState(false);

  useEffect(() => {
    getVariableSetList();

    const currentSearch = new URLSearchParams(location.search);
    currentSearch.set('page', pageNumber);
    history.replace({
      pathname: location.pathname,
      search: currentSearch.toString()
    });
  }, [pageNumber]);

  const getVariableSetList = async () => {
    setIsLoadingData(true);
    setVariableSetList([]);
    setTotalPages(0);
    setTotalVariableSets(0);

    try {
      const variableSetListResponse = await dispatch(
        getAllVariableSets({
          projectId: project.id,
          pageNumber: pageNumber,
          pageSize: 10
        })
      );

      if (variableSetListResponse?.payload) {
        const variableSets = variableSetListResponse.payload?.variable_sets || [];

        // If this is the environments tab, handle primary environment logic
        const enrichedVariableSets = variableSets.map(vs => ({
          ...vs,
          is_primary: vs.id === project.primary_environment_id
        }));

        // If no primary environment is set, mark the first one as primary
        if (!project.primary_environment_id && enrichedVariableSets.length > 0) {
          const firstEnvId = enrichedVariableSets[0].id;
          await dispatch(markVariableSetAsPrimary({
            projectId: project.id,
            variableSetId: firstEnvId
          }));
          enrichedVariableSets[0].is_primary = true;
        }

        setVariableSetList(enrichedVariableSets);

        setTotalPages(variableSetListResponse.payload?.total_pages);
        setTotalVariableSets(variableSetListResponse.payload?.total);
      }
    } catch (error) {
      console.error('Error fetching variable sets:', error);
      openSnackbar({
        message: "Failed to fetch variable sets",
        severity: "error",
      });
    } finally {
      setIsLoadingData(false);
    }
  };

  const onPageChange = (e, value) => {
    setPageNumber(value);
  };

  const onDeleteVariableSet = async (variableSetId) => {
    try {
      await dispatch(deleteVariableSet({ variableSetId }));

      // If we're deleting the primary environment, set a new one
      if (variableSetId === project.primary_environment_id) {
        const remainingEnvs = variableSetList.filter(vs => vs.id !== variableSetId);
        if (remainingEnvs.length > 0) {
          const newPrimaryId = remainingEnvs[0].id;
          await dispatch(markVariableSetAsPrimary({
            projectId: project.id,
            variableSetId: newPrimaryId
          }));
        }
      }

      openSnackbar({
        message: "Variable set deleted successfully.",
        severity: "success",
      });

      if (variableSetList.length === 1 && pageNumber !== 1) {
        onPageChange('', pageNumber - 1 > 0 ? pageNumber - 1 : 1);
      } else {
        getVariableSetList();
      }
    } catch (error) {
      console.error('Error deleting variable set:', error);
      openSnackbar({
        message: "Failed to delete variable set",
        severity: "error",
      });
    }
  };

  const onUpdatePrimaryEnvironment = async (variableSetId) => {
    try {
      await dispatch(markVariableSetAsPrimary({
        projectId: project.id,
        variableSetId: variableSetId
      }));

      setVariableSetList(prevList =>
        prevList.map(vs => ({
          ...vs,
          is_primary: vs.id === variableSetId
        }))
      );

      openSnackbar({
        message: "Environment successfully marked as primary",
        severity: "success"
      });
    } catch (error) {
      console.error('Error updating primary environment:', error);
      openSnackbar({
        message: "Failed to update primary environment",
        severity: "error"
      });
    }
  };

  const createNewVariableSet = async () => {
    const title = `Environment ${variableSetList ? totalVariableSets + 1 : 1
      }`;

    try {
      const variableSetResponse = await dispatch(
        createVariableSet({
          projectId: project.id,
          title: title
        })
      );

      if (variableSetResponse?.payload?.id) {
        MixpanelService.track("environment_created", { variable_set_id: variableSetResponse.payload.id });
        // If this is the first environment, make it primary
        if ((!project.primary_environment_id || variableSetList.length === 0)) {
          await dispatch(markVariableSetAsPrimary({
            projectId: project.id,
            variableSetId: variableSetResponse.payload.id
          }));
        }

        openSnackbar({
          message: `${title} created successfully.`,
          severity: "success",
        });
        history.push(`/environments/${variableSetResponse.payload.id}`);
      }
    } catch (error) {
      console.error('Error creating variable set:', error);
      openSnackbar({
        message: "Failed to create variable set",
        severity: "error",
      });
    }
  };


  const onCloneVariableSet = async (variableSetId) => {
    try {
      setIsLoadingData(true); // Show loading state
      const response = await dispatch(cloneEnvironmentVariable({ variableSetId }));

      if (response?.payload) {
        openSnackbar({
          message: "Variable set cloned successfully",
          severity: "success"
        });
        await getVariableSetList(); // Refresh the list
      }
    } catch (error) {
      console.error('Error cloning variable set:', error);
      openSnackbar({
        message: "Failed to clone variable set",
        severity: "error"
      });
    } finally {
      setIsLoadingData(false); // Hide loading state
    }
  };

  return (
    <MainLayout
      isLoading={isLoading}
      headerText='Environments'
      subtitleText={`Showing ${totalVariableSets || 0} Environment(s)`}
      totalPages={totalPages}
      pageNumber={pageNumber}
      onPageChange={onPageChange}
      rightSideContent={
        <>
          <CustomButton
            startIcon={<Add />}
            onClick={createNewVariableSet}
          >
            Create environment
          </CustomButton>
        </>
      }
    >
      <Box sx={{ maxHeight: "calc(100vh - 230px)", overflow: "auto" }}>
        <VariableSetList
          variableSetList={variableSetList}
          isLoading={isLoadingData}
          pageNumber={pageNumber}
          onDelete={onDeleteVariableSet}
          onClone={onCloneVariableSet}
          onUpdatePrimaryEnvironment={onUpdatePrimaryEnvironment}
        />
      </Box>
    </MainLayout>
  );
};

export default VariableSetListPage;